type IdentifiableMarkup = {
  region?: string | null;
  markupValue?: number | null;
};

const getRegionLabel = (region: string | null | undefined) => {
  if (!region || region.trim() === '') {
    return 'GLOBAL';
  }

  return region.trim();
};

const getValueLabel = (markupValue: number | null | undefined) => {
  return markupValue === null || markupValue === undefined ? null : String(markupValue);
};

const toSuffix = (index: number) => String.fromCharCode(65 + index);

/**
 * Generates stable human-readable identifiers for saved channel markup rows.
 *
 * Rules:
 * - If a region appears only once, use the region alone.
 * - If a region has multiple values, include the markup value.
 * - If both region and value collide, append -A, -B, ...
 */
export function buildChannelMarkupIdentifiers(markups: Array<IdentifiableMarkup>): Array<string> {
  const regionCounts = new Map<string, number>();
  const groupCounts = new Map<string, number>();
  const groupSeen = new Map<string, number>();

  for (const markup of markups) {
    const region = getRegionLabel(markup.region);
    const value = getValueLabel(markup.markupValue);
    const groupKey = value ? `${region}|${value}` : `${region}|__NO_VALUE__`;

    regionCounts.set(region, (regionCounts.get(region) ?? 0) + 1);
    groupCounts.set(groupKey, (groupCounts.get(groupKey) ?? 0) + 1);
  }

  return markups.map((markup) => {
    const region = getRegionLabel(markup.region);
    const value = getValueLabel(markup.markupValue);
    const regionCount = regionCounts.get(region) ?? 0;
    const groupKey = value ? `${region}|${value}` : `${region}|__NO_VALUE__`;
    const groupCount = groupCounts.get(groupKey) ?? 0;
    const groupIndex = groupSeen.get(groupKey) ?? 0;

    groupSeen.set(groupKey, groupIndex + 1);

    if (regionCount === 1) {
      return region;
    }

    const base = value ? `${region}-${value}` : region;
    if (groupCount === 1) {
      return base;
    }

    return `${base}-${toSuffix(groupIndex)}`;
  });
}
